import { FC, useEffect, useRef } from 'react';
import { Content } from '@raydiant/api-client-js';
import DataTable from '../../../../../components/DataTable/DataTable';
import DataTableHeader from '../../../../../components/DataTable/DataTableHeader';
import Text from '@raydiant/raydial/components/Text';
import DataTableCell from '../../../../../components/DataTable/DataTableCell';
import cn from 'classnames';
import moment from 'moment';
import ApplicationIcon from '../ApplicationIcon';
import {
  getPresentationData,
  DEFAULT_SHARED_LIBRARY_FOLDER_DATA,
} from '../../../utils';
import { LibraryPageModalType } from '../../LibraryPageV2';
import Checkbox from 'raydiant-elements/core/Checkbox';
import ContentCardActionButtonGroup from '../LibraryContentCard/ContentCardActionButtonGroup';
import { DEFAULT_QUERY } from '../../../../AddContentPage/AddContentPageV2';

export interface ListViewLayoutProps {
  contents: Content[];
  batchMode: boolean;
  folderOwnerLabel?: string;
  hideMoveOption?: boolean;
  libraryQuery?: typeof DEFAULT_QUERY;
  isTopLevelSharedLibraryFolder?: boolean;
  hideShare?: boolean;
  selectedNodeIds: string[];
  // resourceId of the "See in Library" target to highlight + scroll to ('' = none).
  highlightedResourceId?: string;
  onClick: (content: Content) => void;
  onButtonGroupClick: (resourceId: string) => void;
  onButtonGroupItemClick: (modalType: LibraryPageModalType) => void;
  onClose: () => void;
  onCreateNewPlaylist?: (content: Content) => void;
  onAddToPlaylist?: (content: Content) => void;
  onDuplicateFolder?: (content: Content) => void;
  onDuplicateContent?: (content: Content) => void;
  showDuplicateAndMove?: boolean;
}

const ListViewLayout = ({
  contents,
  batchMode,
  folderOwnerLabel,
  hideMoveOption = false,
  libraryQuery = DEFAULT_QUERY,
  isTopLevelSharedLibraryFolder = false,
  hideShare = false,
  selectedNodeIds,
  highlightedResourceId = '',
  onClick,
  onButtonGroupClick,
  onButtonGroupItemClick,
  onClose,
  onCreateNewPlaylist,
  onAddToPlaylist,
  onDuplicateFolder,
  onDuplicateContent,
  showDuplicateAndMove = false,
}: ListViewLayoutProps) => {
  return (
    <div className="w-full">
      <DataTable<Content>
        className=""
        currentPage={0}
        data={contents}
        showPagination={false}
        headers={
          <tr>
            <DataTableHeader<Content>
              className="!text-left !bg-gray-100 !text-gray-500 !fill-gray-500"
              wrapperClassName="!justify-start"
              onSort={() => {}}
            >
              <Text className="text-base font-semibold pl-8">name</Text>
            </DataTableHeader>
            <DataTableHeader<Content>
              className="!bg-gray-100 !text-gray-500 !fill-gray-500"
              wrapperClassName="!justify-start"
              onSort={() => {}}
              showSort={false}
              sortDirection={undefined}
            >
              <Text className="text-base font-semibold">
                {folderOwnerLabel ?? 'owner'}
              </Text>
            </DataTableHeader>
            <DataTableHeader<Content>
              className="!bg-gray-100 !text-gray-500 !fill-gray-500"
              wrapperClassName="!justify-start"
              onSort={() => {}}
              showSort={false}
              sortDirection={undefined}
            >
              <Text className="text-base font-semibold">created</Text>
            </DataTableHeader>
            <DataTableHeader<Content>
              className="!bg-gray-100 !text-gray-500 !fill-gray-500"
              wrapperClassName="!justify-start"
              onSort={() => {}}
              showSort={false}
              sortDirection={undefined}
            >
              <Text className="text-base font-semibold">last modified</Text>
            </DataTableHeader>
            <DataTableHeader<Content>
              className="!bg-gray-100 !text-gray-500 !fill-gray-500"
              wrapperClassName="!justify-start"
              onSort={() => {}}
              showSort={false}
              sortDirection={undefined}
            ></DataTableHeader>
          </tr>
        }
        onPageChange={() => {}}
        pageCount={1}
        renderRow={(content) => (
          <ListViewRow
            key={content.resourceId}
            content={content}
            highlighted={
              !!highlightedResourceId &&
              content.resourceId === highlightedResourceId
            }
            batchMode={batchMode}
            selectedNodeIds={selectedNodeIds}
            isTopLevelSharedLibraryFolder={isTopLevelSharedLibraryFolder}
            hideMoveOption={hideMoveOption}
            libraryQuery={libraryQuery}
            hideShare={hideShare}
            showDuplicateAndMove={showDuplicateAndMove}
            onClick={onClick}
            onButtonGroupClick={onButtonGroupClick}
            onButtonGroupItemClick={onButtonGroupItemClick}
            onClose={onClose}
            onCreateNewPlaylist={onCreateNewPlaylist}
            onAddToPlaylist={onAddToPlaylist}
            onDuplicateFolder={onDuplicateFolder}
            onDuplicateContent={onDuplicateContent}
          />
        )}
      />
    </div>
  );
};

interface ListViewRowProps {
  content: Content;
  // "See in Library" target: tints the row + scrolls it into view once.
  highlighted: boolean;
  batchMode: boolean;
  selectedNodeIds: string[];
  isTopLevelSharedLibraryFolder?: boolean;
  hideMoveOption?: boolean;
  libraryQuery?: typeof DEFAULT_QUERY;
  hideShare?: boolean;
  showDuplicateAndMove?: boolean;
  onClick: (content: Content) => void;
  onButtonGroupClick: (resourceId: string) => void;
  onButtonGroupItemClick: (modalType: LibraryPageModalType) => void;
  onClose: () => void;
  onCreateNewPlaylist?: (content: Content) => void;
  onAddToPlaylist?: (content: Content) => void;
  onDuplicateFolder?: (content: Content) => void;
  onDuplicateContent?: (content: Content) => void;
}

// Extracted so the highlight scroll can use a useRef + useEffect (fires once
// when `highlighted` flips true), instead of an inline ref callback that would
// re-fire scrollIntoView on every table re-render.
const ListViewRow: FC<ListViewRowProps> = ({
  content,
  highlighted,
  batchMode,
  selectedNodeIds,
  isTopLevelSharedLibraryFolder = false,
  hideMoveOption = false,
  libraryQuery = DEFAULT_QUERY,
  hideShare = false,
  showDuplicateAndMove = false,
  onClick,
  onButtonGroupClick,
  onButtonGroupItemClick,
  onClose,
  onCreateNewPlaylist,
  onAddToPlaylist,
  onDuplicateFolder,
  onDuplicateContent,
}) => {
  const rowRef = useRef<HTMLTableRowElement>(null);
  useEffect(() => {
    if (highlighted) {
      rowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [highlighted]);

  return (
    <tr
      ref={rowRef}
      onClick={() => onClick(content)}
      // Row tint marks the "See in Library" target (distinct from the
      // batch-select checkbox).
      className={cn('cursor-pointer', highlighted && 'bg-blue-50')}
    >
      <DataTableCell
        hasSort
        className="p-2.5"
        wrapperClassName="text-left flex items-center"
      >
        {batchMode && (
          <div className="w-4 mr-4">
            <Checkbox
              checked={selectedNodeIds.includes(
                `${content.resourceType}|${content.id}`,
              )}
            />
          </div>
        )}
        <ApplicationIcon
          className="mr-2.5"
          // Use the shared library icon for top-level shared library folders instead of the generic folder icon
          url={
            isTopLevelSharedLibraryFolder && content.resourceType === 'folder'
              ? DEFAULT_SHARED_LIBRARY_FOLDER_DATA.applicationIconUrl
              : getPresentationData(content).applicationIconUrl
          }
        />
        <Text className="text-lg w-[191px] truncate">{content.name}</Text>
      </DataTableCell>
      <DataTableCell
        className="p-2.5"
        wrapperClassName="min-w-[264px] text-left"
      >
        <Text className="w-[138px] text-lg truncate">
          {content.ownedBy?.name}
        </Text>
      </DataTableCell>
      <DataTableCell
        className="p-2.5"
        wrapperClassName="min-w-[160px] text-left"
      >
        <Text
          className="w-[140px] text-lg truncate"
          title={moment(content.createdAt).format('lll')}
        >
          {moment(content.createdAt).format('lll')}
        </Text>
      </DataTableCell>
      <DataTableCell
        className="p-2.5"
        wrapperClassName="min-w-[160px] text-left"
      >
        <Text
          className="w-[140px] text-lg truncate"
          title={moment(content.updatedAt).format('lll')}
        >
          {moment(content.updatedAt).format('lll')}
        </Text>
      </DataTableCell>
      <DataTableCell className="p-2.5 pr-7">
        <div className="">
          <ContentCardActionButtonGroup
            content={content}
            placement="bottom end"
            onButtonGroupClick={onButtonGroupClick}
            onButtonGroupItemClick={onButtonGroupItemClick}
            onButtonGroupClose={onClose}
            onCreateNewPlaylist={onCreateNewPlaylist}
            onAddToPlaylist={onAddToPlaylist}
            onDuplicateFolder={onDuplicateFolder}
            onDuplicateContent={onDuplicateContent}
            hideMoveOption={hideMoveOption}
            libraryQuery={libraryQuery}
            isTopLevelSharedLibraryFolder={
              isTopLevelSharedLibraryFolder && content.resourceType === 'folder'
            }
            hideShare={hideShare}
            showDuplicateAndMove={showDuplicateAndMove}
          />
        </div>
      </DataTableCell>
    </tr>
  );
};

export default ListViewLayout;
